import React, { useEffect, useState } from 'react';
import './landingPage.scss';

import MenuIcon from './logo.png';
import FallLeaf from './fallLeaf.png';
import SnowFlake from './snowFlake.png';
import SpringFlower from './springFlower1.png';

// Type for season names
type Season = 'winter' | 'spring' | 'summer' | 'fall';

// 시간별 비 확률 인터페이스
interface HourlyRain {
  hour: number;
  chance: number;
}

// Weather data interface
interface WeatherData {
  temperature: number;
  feelsLike: number;
  description: string;
  icon: string;
  humidity: number;
  windSpeed: number;
  cityName: string;
  rainChance: number;
  hourlyRainChance: HourlyRain[]; // 🌧️ 다음 5시간 비 올 확률
}

function LandingPage() {
  const [season, setSeason] = useState<Season>('fall');
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [weatherLoading, setWeatherLoading] = useState<boolean>(true);
  const [weatherError, setWeatherError] = useState<string | null>(null);

  const API_KEY = process.env.REACT_APP_WEATHER_API_KEY;
  const ATLANTA_LAT = 33.749;
  const ATLANTA_LON = -84.388;

  // 🔹 Determine season dynamically by month
  const getSeason = (): Season => {
    const month = new Date().getMonth();

    if (month === 11 || month === 0 || month === 1) return 'winter';
    if (month >= 2 && month <= 4) return 'spring';
    if (month >= 5 && month <= 7) return 'summer';
    if (month >= 8 && month <= 10) return 'fall';
    return 'fall';
  };

  // 🔹 오늘 날짜 포맷 함수
  const getFormattedDate = (): string => {
    const today = new Date();
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    };
    return today.toLocaleDateString('en-US', options);
  };

  // 🔹 Fetch weather data - One Call API 3.0
  const fetchWeather = async () => {
    if (!API_KEY) {
      setWeatherError('API 키가 없습니다');
      setWeatherLoading(false);
      return;
    }

    try {
      setWeatherLoading(true);
      setWeatherError(null);

      const response = await fetch(
        `https://api.openweathermap.org/data/3.0/onecall?lat=${ATLANTA_LAT}&lon=${ATLANTA_LON}&appid=${API_KEY}&units=imperial&exclude=minutely,alerts`
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch weather data');
      }

      const data = await response.json();

      // 🌧️ 다음 5시간 비 올 확률 추출
      const hourlyRainData: HourlyRain[] = [];
      const currentHour = new Date().getHours();
      
      for (let i = 0; i < 5; i++) {
        if (data.hourly?.[i]) {
          const hour = (currentHour + i + 1) % 24;
          hourlyRainData.push({
            hour: hour,
            chance: Math.round((data.hourly[i].pop || 0) * 100)
          });
        }
      }

      setWeather({
        temperature: Math.round(data.current.temp),
        feelsLike: Math.round(data.current.feels_like),
        description: data.current.weather[0].description,
        icon: data.current.weather[0].icon,
        humidity: data.current.humidity,
        windSpeed: Math.round(data.current.wind_speed),
        cityName: 'Atlanta',
        rainChance: Math.round((data.hourly?.[0]?.pop || 0) * 100),
        hourlyRainChance: hourlyRainData,
      });

    } catch (error) {
      console.error('Weather fetch error:', error);
      if (error instanceof Error) {
        setWeatherError(error.message);
      } else {
        setWeatherError('날씨 정보를 불러올 수 없습니다');
      }
    } finally {
      setWeatherLoading(false);
    }
  };

  // 🔹 On mount, refresh season and fetch weather
  useEffect(() => {
    setSeason(getSeason());
    fetchWeather();

    const seasonTimer = setInterval(() => {
      setSeason(getSeason());
    }, 1000 * 60 * 60);

    const weatherTimer = setInterval(() => {
      fetchWeather();
    }, 1000 * 60 * 30);

    return () => {
      clearInterval(seasonTimer);
      clearInterval(weatherTimer);
    };
  }, []);

  // 🔹 Weather icon URL
  const getWeatherIconUrl = (iconCode: string) =>
    `https://openweathermap.org/img/wn/${iconCode}@2x.png`;

  // 🔹 시간 포맷 (12시간제)
  const formatHour = (hour: number): string => {
    if (hour === 0) return '12AM';
    if (hour === 12) return '12PM';
    if (hour < 12) return `${hour}AM`;
    return `${hour - 12}PM`;
  };

  // 🔹 Get weather description in Korean
  const getKoreanWeatherDescription = (description: string): string => {
    const weatherMap: { [key: string]: string } = {
      'clear sky': '맑음',
      'few clouds': '구름 조금',
      'scattered clouds': '구름 흩어짐',
      'broken clouds': '구름 많음',
      'overcast clouds': '흐림',
      'shower rain': '소나기',
      'rain': '비',
      'light rain': '이슬비',
      'moderate rain': '비',
      'heavy intensity rain': '폭우',
      'thunderstorm': '천둥번개',
      'thunderstorm with heavy rain': '천둥번개 폭우',
      'snow': '눈',
      'light snow': '가벼운 눈',
      'mist': '안개',
      'fog': '짙은 안개',
      'haze': '연무',
    };
    return weatherMap[description.toLowerCase()] || description;
  };

  // 🔹 Golf advice based on weather
  const getGolfAdvice = (weather: WeatherData): string => {
    if (weather.rainChance >= 70) {
      return '🌧️ 1시간 내 비 올 확률 높음! 실내 연습 추천!';
    }
    if (weather.rainChance >= 40) {
      return '🌂 우산 챙기세요! 곧 비 올 수도 있어요';
    }

    if (weather.description.includes('rain') || weather.description.includes('thunderstorm')) {
      return '🌧️ 비 오는 중! 실내 연습 추천!';
    }

    if (weather.temperature < 40) {
      return '🧥 추워요! 따뜻하게 입고 나가세요!';
    }
    if (weather.temperature > 90) {
      return '🥵 더워요! 수분 섭취 필수!';
    }

    if (weather.windSpeed > 15) {
      return '💨 바람 강함! 클럽 선택 주의!';
    }

    if (weather.description.includes('clear')) {
      return '⛳ 완벽한 골프 날씨!';
    }

    return '⛳ 골프 치기 좋은 날씨!';
  };

  // 🔹 Weather Widget
  const renderWeatherWidget = () => {
    if (weatherLoading) {
      return (
        <div className="weatherWidget loading">
          <div className="weatherSpinner"></div>
          <span>로딩중...</span>
        </div>
      );
    }

    if (weatherError) {
      return (
        <div className="weatherWidget error">
          <span>{weatherError}</span>
          <button onClick={fetchWeather} className="retryButton">
            재시도
          </button>
        </div>
      );
    }

    if (!weather) return null;

    return (
      <div className="weatherWidget">
        <div className="dateInfo">
          {getFormattedDate()}
        </div>

        <div className="weatherTop">
          <div className="locationInfo">
            <span className="locationIcon">📍</span>
            <span className="locationName">Atlanta, GA</span>
          </div>

          <div className="weatherLeft">
            <img
              src={getWeatherIconUrl(weather.icon)}
              alt={weather.description}
              className="weatherIcon"
            />
            <span className="temperature">{weather.temperature}°F</span>
            <span className="weatherDescription">
              {getKoreanWeatherDescription(weather.description)}
            </span>
          </div>

          <div className="weatherRight">
            {/* 🌧️ 비 확률 - 커스텀 툴팁 */}
            <div className="rainChanceWrapper">
              <span className="detailItem rainChance">
                🌧️{weather.rainChance}%
              </span>
              <div className="rainTooltip">
                <div className="tooltipTitle">다음 5시간 비 확률</div>
                {weather.hourlyRainChance.map((item, index) => (
                  <div key={index} className="tooltipRow">
                    <span className="tooltipHour">{formatHour(item.hour)}</span>
                    <div className="tooltipBarContainer">
                      <div 
                        className="tooltipBar" 
                        style={{ width: `${item.chance}%` }}
                      />
                    </div>
                    <span className="tooltipPercent">{item.chance}%</span>
                  </div>
                ))}
              </div>
            </div>
            <span className="detailItem">💨{weather.windSpeed}mph</span>
          </div>
        </div>

        <div className="golfAdvice">
          {getGolfAdvice(weather)}
        </div>
      </div>
    );
  };

  // 🔹 Helper for rendering N decorative images
  const renderDecorations = (count: number, imgSrc: string, baseClass: string) =>
    Array.from({ length: count }).map((_, index) => (
      <div className={baseClass} key={`${baseClass}-${index}`}>
        <div className="inner">
          <img src={imgSrc} alt={`${baseClass}-${index}`} className={`${baseClass}1`} />
        </div>
      </div>
    ));

  // 🔹 Seasonal decorations
  const renderSeasonalEffect = () => {
    const screenWidth = window.innerWidth;
    let bulbCount = 20;

    if (screenWidth < 500) bulbCount = 10;
    else if (screenWidth < 800) bulbCount = 15;
    else if (screenWidth < 1200) bulbCount = 16;

    switch (season) {
      case 'winter':
        return (
          <div className="winterScene">
            <div className="snowflakes">
              {renderDecorations(25, SnowFlake, 'snowflake')}
            </div>
            <div className="christmasLightsWrapper">
              {Array.from({ length: bulbCount }).map((_, i, arr) => {
                const progress = i / (arr.length - 1);
                const left = `${progress * 100}%`;
                const maxCurve = screenWidth < 600 ? 22 : screenWidth < 1000 ? 10 : 25;
                const curveHeight = Math.sin(progress * Math.PI) * maxCurve;
                const translateY = `calc(-80% + ${curveHeight}px)`;

                return (
                  <div
                    key={i}
                    className="lightBulb"
                    style={{
                      left,
                      transform: `translate(100%, ${translateY})`,
                    }}
                  />
                );
              })}
            </div>
          </div>
        );

      case 'spring': {
        let flowerCount = 10;
        if (screenWidth < 500) flowerCount = 8;
        else if (screenWidth < 800) flowerCount = 10;

        return (
          <div className="springFlowers">
            {Array.from({ length: flowerCount }).map((_, index) => {
              const left = `${(index / (flowerCount - 1)) * 100}%`;
              return (
                <div key={index} className="flower" style={{ left }}>
                  <img src={SpringFlower} alt={`flower-${index}`} />
                </div>
              );
            })}
          </div>
        );
      }

      case 'fall':
        return (
          <div className="fallLeaves">
            {renderDecorations(15, FallLeaf, 'fallLeaf')}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`bodyWrapper ${season} ${season === 'winter' ? '' : 'warmWhite'}`}>
      <div className="weatherContainer">
        {renderWeatherWidget()}
      </div>

      <div className="landingPageWrapper">
        <span className="BangName">
          아틀란타 거침없는 골프 초보골프 백순이 백돌이 모여라 !
        </span>

        <div className="welcome_sign_fade">
          <h1>
            환영합니다 <br /> 영어로 웰컴
          </h1>
        </div>

        <div className="menu">
          <nav>
            <li className="menuList">
              <a href="/golf_101_list" className="golf_101_link">
                <div className="golf_101_container">
                  <img className="golf_101_icon" src={MenuIcon} alt="Golf 101" />
                  <span className="golf_101_menu_text">골프 101 보러가기</span>
                </div>
              </a>
            </li>
          </nav>
        </div>
      </div>

      {renderSeasonalEffect()}
    </div>
  );
}

export default LandingPage;